import React, { useState, useRef } from "react";

interface HighlightedTextAreaProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
}

const HighlightedTextArea: React.FC<HighlightedTextAreaProps> = ({ 
  value, 
  onChange, 
  placeholder, 
  rows = 4,
  disabled = false
}: HighlightedTextAreaProps) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [focused, setFocused] = useState(false);
  
  // Escape HTML entities to prevent XSS
  const escapeHtml = (text: string): string => {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  };

  const decodeHtmlEntities = (text: string): string => {
    const div = document.createElement("div");
    div.innerHTML = text;
    return div.textContent || "";
  };

  // Create the highlighted version of the text
  const getHighlightedContent = (text: string) => {
    if (!text) return [];
    const escapedText = escapeHtml(text);
    const nodes: React.ReactNode[] = [];
    const regex = /{([^{}]+)}/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let keyIndex = 0;

    while ((match = regex.exec(escapedText)) !== null) {
      const matchStart = match.index;
      const matchEnd = matchStart + match[0].length;
      if (matchStart > lastIndex) {
        nodes.push(decodeHtmlEntities(escapedText.slice(lastIndex, matchStart)));
      }
      nodes.push(
        <span
          key={`highlight-${keyIndex}`}
          style={{ color: "#21b892", fontWeight: "bold" }}
        >
          {decodeHtmlEntities(match[0])}
        </span>
      );
      lastIndex = matchEnd;
      keyIndex += 1;
    }

    if (lastIndex < escapedText.length) {
      nodes.push(decodeHtmlEntities(escapedText.slice(lastIndex)));
    }

    return nodes;
  };
  
  // Handle key events and ensure cursor position is maintained
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Tab") {
      e.preventDefault();
      
      const textarea = textAreaRef.current;
      if (!textarea) return;
      
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      // Insert tab at cursor position
      const newValue = value.substring(0, start) + "  " + value.substring(end);
      onChange({ target: { value: newValue } } as React.ChangeEvent<HTMLTextAreaElement>);
      
      // Set cursor position after the inserted tab
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      }, 0);
    }
  };
  
  return (
    <div 
      style={{ 
        position: "relative",
        width: "100%",
        boxSizing: "border-box",
        margin: "0",
      }}
      className="ant-input-wrapper"
    >
      {/* Real textarea (invisible but functional) */}
      <textarea
        ref={textAreaRef}
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          padding: "4px 11px",
          color: "transparent",
          caretColor: "black",
          background: disabled ? "#f5f5f5" : "transparent",
          border: `1px solid ${focused && !disabled ? "#21b892" : "#d9d9d9"}`,
          borderRadius: "2px",
          fontFamily: "inherit",
          fontSize: "inherit",
          lineHeight: "inherit",
          resize: "vertical",
          zIndex: 2,
          outline: focused && !disabled ? "none" : undefined,
          boxShadow: focused && !disabled ? "0 0 0 2px rgba(33, 184, 146, 0.2)" : "none",
          boxSizing: "border-box",
          cursor: disabled ? "not-allowed" : "text",
        }}
        placeholder={placeholder}
        rows={rows}
        className="ant-input"
      />
      
      {/* Highlighted display layer (read-only) */}
      <div
        style={{
          width: "100%",
          minHeight: `${rows * 1.5}em`,
          padding: "4px 11px",
          border: "1px solid transparent",
          borderRadius: "2px",
          fontFamily: "inherit",
          fontSize: "inherit",
          lineHeight: "inherit",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "break-word",
          color: disabled ? "#bfbfbf" : (value ? "inherit" : "#bfbfbf"),
          zIndex: 1,
          boxSizing: "border-box",
        }}
      >
        {value ? getHighlightedContent(value) : (placeholder || "")}
      </div>
    </div>
  );
};

export default HighlightedTextArea; 